import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json'
    },
    maxRedirects: 5
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            const refreshToken = localStorage.getItem('refresh_token');

            if (!refreshToken) {
                localStorage.removeItem('token');
                window.location.href = '/login';
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await axios.post(`${API_URL}/api/auth/refresh`, {
                    refresh_token: refreshToken
                });
                const { access_token, refresh_token: newRefresh } = response.data;
                localStorage.setItem('token', access_token);
                if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
                api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                processQueue(null, access_token);
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem('token');
                localStorage.removeItem('refresh_token');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    refresh: (data) => api.post('/auth/refresh', data),
    me: () => api.get('/auth/me')
};

// Users
export const usersAPI = {
    getAll: () => api.get('/users/'),
    create: (data) => api.post('/users/create', data),
    updateRole: (userId, role) => api.put(`/users/${userId}/role?role=${role}`),
    delete: (userId) => api.delete(`/users/${userId}`),
    changePassword: (data) => api.post('/users/change-password', data)
};

// Products
export const productsAPI = {
    getAll: (params) => api.get('/products/', { params }),
    getBySku: (sku) => api.get(`/products/sku/${encodeURIComponent(sku)}`),
    get: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/products/', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`)
};

// Categories
export const categoriesAPI = {
    getAll: () => api.get('/categories/'),
    create: (data) => api.post('/categories/', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`)
};

// Suppliers
export const suppliersAPI = {
    getAll: () => api.get('/suppliers/'),
    create: (data) => api.post('/suppliers/', data),
    update: (id, data) => api.put(`/suppliers/${id}`, data),
    delete: (id) => api.delete(`/suppliers/${id}`),
    // Purchase Orders
    getOrders: () => api.get('/suppliers/orders'),
    createOrder: (data) => api.post('/suppliers/orders', data),
    receiveOrder: (orderId) => api.put(`/suppliers/orders/${orderId}/receive`)
};

// Inventory
export const inventoryAPI = {
    adjust: (data) => api.post('/inventory/adjust', data),
    getHistory: (productId) => api.get('/inventory/history', { params: { product_id: productId } }),
    getLowStock: () => api.get('/inventory/low-stock')
};

// Sales
export const salesAPI = {
    getAll: (params) => api.get('/sales/', { params }),
    get: (id) => api.get(`/sales/${id}`),
    create: (data) => api.post('/sales/', data),
    delete: (id) => api.delete(`/sales/${id}`)
};

// Reports
export const reportsAPI = {
    getDashboard: () => api.get('/reports/dashboard'),
    getSalesByDate: (days) => api.get('/reports/sales-by-date', { params: { days } }),
    getTopProducts: (limit) => api.get('/reports/top-products', { params: { limit } }),
    getSalesByCategory: () => api.get('/reports/sales-by-category')
};

// Payments
export const paymentsAPI = {
    stripeCreateSession: (data) => api.post('/payments/stripe/create-session', data),
    stripeConfirm: (data) => api.post('/payments/stripe/confirm', data)
};

// Subscriptions
export const subscriptionsAPI = {
    getPlans: () => api.get('/subscriptions/plans'),
    createCheckout: (data) => api.post('/subscriptions/create-checkout', data),
    getStatus: () => api.get('/subscriptions/status'),
    verify: (sessionId) => api.post(`/subscriptions/verify/${sessionId}`),
    startTrial: (data) => api.post('/subscriptions/trial-start', data),
};

// Stores
export const storesAPI = {
    create: (data) => api.post('/stores/', data),
    startTrial: (data) => api.post('/stores/start-trial', data),
    getMyStore: () => api.get('/stores/my-store'),
    getAll: () => api.get('/stores/'),
    approve: (storeId) => api.put(`/stores/${storeId}/approve`),
    inviteUser: (data) => api.post('/stores/invite-user', data),
    getUsers: () => api.get('/stores/users'),
    getSettings: () => api.get('/stores/settings'),
    updateSettings: (data) => api.put('/stores/settings', data),
    getInfo: () => api.get('/stores/info'),
    updateInfo: (data) => api.put('/stores/info', data),
    getBusinessTypes: () => api.get('/stores/business-types'),
    applyTemplate: (data) => api.post('/stores/apply-template', data)
};

// Admin (super admin only)
export const adminAPI = {
    getStats: () => api.get('/admin/stats'),
    listStores: () => api.get('/admin/stores'),
    getStoreDetail: (storeId) => api.get(`/admin/stores/${storeId}`),
    addNote: (storeId, note) => api.post(`/admin/stores/${storeId}/notes`, { note }),
    impersonate: (userId) => api.post(`/admin/impersonate/${userId}`),
    suspendStore: (storeId) => api.put(`/admin/stores/${storeId}/suspend`),
    activateStore: (storeId) => api.put(`/admin/stores/${storeId}/activate`)
};

// Timeclock
export const timeclockAPI = {
    clockIn: (data) => api.post('/timeclock/clock-in', data),
    clockOut: (data) => api.post('/timeclock/clock-out', data),
    getActive: () => api.get('/timeclock/active'),
    // PIN management
    createPin: (data) => api.post('/timeclock/pins', data),
    listPins: () => api.get('/timeclock/pins'),
    deletePin: (userId) => api.delete(`/timeclock/pins/${userId}`),
    // Entries
    getEntries: (params) => api.get('/timeclock/entries', { params }),
    editEntry: (id, data) => api.put(`/timeclock/entries/${id}`, data),
    createManualEntry: (data) => api.post('/timeclock/entries/manual', data),
    // Settings
    getSettings: () => api.get('/timeclock/settings'),
    updateSettings: (data) => api.put('/timeclock/settings', data),
    getPublicSettings: (storeId) => api.get(`/timeclock/settings/public/${storeId}`)
};

// Barcode lookup
export const barcodeAPI = {
    lookup: (barcode) => api.post('/barcode/lookup', { barcode })
};

export default api;
